import { type AbstractMesh, Color3, Mesh, MeshBuilder, type Scene, StandardMaterial, TransformNode } from "@babylonjs/core";
import type { MarkerView, PlotInfo } from "../net/api";
import { clip, toWorld } from "../world/markers";
import { buildPrimitive } from "./primitives";

export const OPEN_COLOR = "#ff9a3c";
export const DONE_COLOR = "#4cd07d";
const POLE = 1.2;

type Vec = { x: number; y: number; z: number };
type LabelMaker = (scene: Scene, name: string, text: string) => Mesh | null;

const billboardLabel: LabelMaker = (scene, name, text) => {
  const m = buildPrimitive(scene, name, { shape: "label", size: [1.6, 0.3, 0.01], color: "#f4efe6", text });
  m.billboardMode = Mesh.BILLBOARDMODE_ALL;
  m.isPickable = false;
  return m;
};

/** One glowing pin per marker of the plots in $markers (spec 2h §2.4); rebuilt only when it changes. */
export class MarkerPins {
  private readonly pins = new Map<string, { root: TransformNode; key: string }>();
  private preview: TransformNode | null = null;

  constructor(
    private readonly scene: Scene,
    private readonly height: (x: number, z: number) => number,
    private readonly makeLabel: LabelMaker = billboardLabel,
  ) {}

  sync(plots: PlotInfo[], markers: Record<string, MarkerView[]>): void {
    const seen = new Set<string>();
    for (const [plotId, list] of Object.entries(markers)) {
      const plot = plots.find((p) => p.id === plotId);
      if (!plot) continue;
      for (const m of list) {
        const at = toWorld(plot, m.pos, this.height);
        const label = clip(m.body, 16);
        const key = `${m.status}|${label}|${at.x}|${at.y}|${at.z}`;
        seen.add(m.id);
        const cur = this.pins.get(m.id);
        if (cur?.key === key) continue;
        cur?.root.dispose(false, true);
        const color = m.status === "done" ? DONE_COLOR : OPEN_COLOR;
        this.pins.set(m.id, { root: this.build(`marker:${m.id}`, at, color, label, { markerId: m.id, plotId }), key });
      }
    }
    for (const [id, p] of this.pins) {
      if (!seen.has(id)) { p.root.dispose(false, true); this.pins.delete(id); }
    }
  }

  /** The half-transparent pin where a marker is being written; null removes it. */
  showPreview(at: Vec | null): void {
    this.preview?.dispose(false, true);
    this.preview = at ? this.build("marker:preview", at, OPEN_COLOR, null, null, 0.5) : null;
  }

  private build(name: string, at: Vec, color: string, label: string | null, meta: { markerId: string; plotId: string } | null, alpha = 1): TransformNode {
    const root = new TransformNode(name, this.scene);
    root.position.set(at.x, at.y, at.z);
    const mat = new StandardMaterial(name + ":mat", this.scene);
    mat.emissiveColor = Color3.FromHexString(color);
    mat.diffuseColor = Color3.Black();
    mat.specularColor = Color3.Black();
    mat.disableLighting = true;
    mat.alpha = alpha;
    const pin = MeshBuilder.CreateCylinder(name + ":pin", { height: POLE, diameter: 0.06, tessellation: 8 }, this.scene);
    pin.position.y = POLE / 2;
    const head = MeshBuilder.CreatePolyhedron(name + ":head", { type: 1, size: 0.14 }, this.scene);
    head.position.y = POLE + 0.12;
    for (const m of [pin, head]) {
      m.parent = root;
      m.material = mat;
      m.isPickable = false;
      m.checkCollisions = false;
    }
    if (meta) {
      // the pin is 6 cm thin; an invisible fatter cylinder is what the look-at ray hits
      const hit = MeshBuilder.CreateCylinder(name + ":hit", { height: POLE + 0.3, diameter: 0.4, tessellation: 8 }, this.scene);
      hit.parent = root;
      hit.position.y = (POLE + 0.3) / 2;
      hit.visibility = 0;
      hit.checkCollisions = false;
      hit.metadata = { ...meta };
    }
    if (label) {
      const l = this.makeLabel(this.scene, name + ":label", label);
      if (l) { l.parent = root; l.position.y = POLE + 0.5; }
    }
    return root;
  }
}

/** The marker a picked mesh belongs to, for the look-at check in keys.ts. */
export function markerAt(mesh: AbstractMesh | null | undefined): { plotId: string; id: string } | null {
  const md = mesh?.metadata as { markerId?: string; plotId?: string } | null | undefined;
  return md?.markerId && md.plotId ? { plotId: md.plotId, id: md.markerId } : null;
}
